// Industry pages (#/industries, #/industries/<slug>) — 2026-11-02.
//
// One page per industry, the single source for its valuation and what we
// know about the trade (routes/industry_pages.py). The valuation is Zach's
// Low / Average / High grid, saved by his valuation skill through the
// connector tool save_industry_valuation, or built by Atlas on the agent
// worker (Build valuation, 202611022100) as a draft that Manage industries
// approves. Only Manage industries edits here (builds, approvals, files,
// which version is current, the target rules).
//
// The build states follow Vercel / GitHub Actions (queued → a live step
// trail → done or failed with the trail kept); the draft follows a GitHub
// review (the draft next to what it changes, Approve or Discard).
//
// Layout borrowed from Atomic CRM's CompanyShow (one main column, titled
// blocks that hide when empty) and Frappe CRM's deal side panel (collapsible
// sections with a count in the header); the version list follows Outline's
// history (absolute dates, "Current" on one row). Sections are window.Section
// with ids, so each viewer's open/closed choice is remembered.

const IND_TIERS = [['low', 'Low'], ['average', 'Average'], ['high', 'High']];
const IND_TIER_TIP = {
  1: 'Tier 1 — major multiple driver',
  2: 'Tier 2 — material underwriting driver',
  3: 'Tier 3 — supporting factor',
};
const IND_NOTES = [
  ['sector', 'Sector definition'], ['economics', 'How companies make money'], ['drivers', 'Primary valuation drivers'],
  ['risks', 'Primary risks'], ['environment', 'Current M&A environment'], ['history', 'Historical valuation observations'],
  ['evidence', 'Valuation evidence used'],
];
const IND_FILE_SLOTS = [['valuation_slide', 'Valuation slide'], ['owner_slides', 'Owner slides'], ['other', 'Other files']];
const IND_CAMPAIGN_HUE = { running: 'green', paused: 'amber', draft: 'slate', done: 'blue', stopped: 'rose' };
const IND_SECTIONS = [['valuation', 'Valuation'], ['numbers', 'Our numbers'], ['market', 'Market'], ['files', 'Files'], ['outreach', 'Outreach']];
const IND_BUILD_STEPS = [['brief', 'Reading Atlas'], ['research', 'Researching'], ['write', 'Writing'], ['check', 'Checking'], ['save', 'Saving']];
const IND_WHY = {
  took_to_market: ['Took to market', 'A company we took to market in this industry'],
  qualified: ['Qualified', 'A qualified company in this industry'],
  in_industry: ['In this industry', 'A company in this industry with a website'],
  picked: ['Picked', 'Picked for this build'],
};
// The builder's step kinds → the shared RunSteps icons.
const IND_STEP_KIND = { buyers: 'people', check: 'verdict', save: 'verdict' };
const indActive = (b) => !!b && (b.status === 'queued' || b.status === 'running');

const indNum = (v) => (v == null ? null : Number(v).toFixed(1).replace(/\.0$/, ''));
const indX = (v) => (v == null ? '—' : `${indNum(v)}×`);
// '5–7x', '7x', '12x+' — the same label the API writes (industry_valuations.tier_label).
const indTier = (t) => {
  if (!t || t.min == null) return '—';
  const body = t.max == null || t.max === t.min ? `${indNum(t.min)}x` : `${indNum(t.min)}–${indNum(t.max)}x`;
  return body + (t.open ? '+' : '');
};
const indHost = (url) => (url || '').replace(/^https?:\/\/(www\.)?/i, '').split('/')[0];
const indDate = (iso) => (iso ? window.fmtDate(iso) : '—');
const indMoney = (n) => (n == null ? '—' : window.fmtMoney(n));
const indMetric = (v) => (v && (v.metric_label || { ebitda: 'EBITDA', revenue: 'Revenue', arr: 'ARR' }[v.metric])) || '';
const indCap = { fontSize: 10.5, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-4)' };
const indTh = { ...indCap, padding: '6px 8px', textAlign: 'left', whiteSpace: 'nowrap', fontWeight: 500 };
const indTd = { padding: '6px 8px', verticalAlign: 'top' };
const indLink = { color: 'var(--text)', textDecoration: 'none', fontWeight: 500 };
const indMuted = { fontSize: 12, color: 'var(--text-3)' };
const indAgo = (iso) => {
  if (!iso) return '';
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 1) return 'just now';
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  return h < 24 ? `${h} h ago` : indDate(iso);
};
// A bar's actions: one group on the right that wraps as a unit.
const indActions = { display: 'inline-flex', alignItems: 'center', gap: 8, marginLeft: 'auto', flexShrink: 0 };
const indTextBtn = { border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5,
                     fontWeight: 600, color: 'inherit', textDecoration: 'underline' };
const indPulseStyle = () => {
  if (typeof document === 'undefined' || document.getElementById('atlas-industry-kf')) return;
  const st = document.createElement('style');
  st.id = 'atlas-industry-kf';
  st.textContent = '@keyframes atlasIndustryPulse { 0%,100% { opacity: 1 } 50% { opacity: .3 } } '
    + '@media (prefers-reduced-motion: reduce) { [data-ind-pulse] { animation: none !important } }';
  document.head.appendChild(st);
};

const useIndWidth = () => {
  const [w, setW] = React.useState(() => window.innerWidth);
  React.useEffect(() => {
    const on = () => setW(window.innerWidth);
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return w;
};

// The app shell clips its main area; each page scrolls itself.
const IndScroller = ({ narrow, children, scrollRef }) => (
  <div ref={scrollRef} style={{ height: '100%', overflowY: 'auto', WebkitOverflowScrolling: 'touch' }}>
    <div style={{ padding: narrow ? '14px 16px 40px' : '18px 22px 48px', maxWidth: 1240, boxSizing: 'border-box' }}>{children}</div>
  </div>
);

const IndEmpty = ({ children, action }) => (
  <div style={{ padding: '18px 14px', fontSize: 12.5, color: 'var(--text-3)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
    <span>{children}</span>{action}
  </div>
);

// A table that scrolls sideways inside its section, never the page.
const IndTable = ({ head, children, minWidth }) => (
  <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
    <table style={{ width: '100%', minWidth, borderCollapse: 'collapse', fontSize: 12.5 }}>
      <thead><tr>{head.map(([label, align], i) => <th key={i} style={{ ...indTh, textAlign: align || 'left' }}>{label}</th>)}</tr></thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

const IndLeadPill = ({ lead }) => {
  if (!lead) return <window.Pill hue="amber" sz="sm" title="No one covers this industry (Settings › Industries)">No coverage lead</window.Pill>;
  return (
    <window.Pill hue={lead.active ? 'slate' : 'amber'} sz="sm" title={lead.active ? 'Coverage lead' : 'Coverage lead is inactive'}>
      {lead.name}{lead.active ? '' : ' · inactive'}
    </window.Pill>
  );
};

// ── The list (#/industries) ───────────────────────────────────────────────────

const IndustriesView = () => {
  const width = useIndWidth();
  const narrow = width < 720;
  const [data, setData] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [q, setQ] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const alive = React.useRef(true);
  React.useEffect(() => () => { alive.current = false; }, []);
  const load = React.useCallback(() => window.AtlasApi.listIndustryPages()
    .then((r) => { if (alive.current) setData(r.items || []); return r.items || []; })
    .catch((e) => { if (alive.current) setErr(e.message || String(e)); return []; }), []);
  React.useEffect(() => { load(); }, [load]);
  const liveBuilds = (data || []).filter((r) => r.build).length;
  // Builds in flight: refresh the list until they end.
  window.usePolling(async () => {
    const items = await load();
    return items.map((r) => `${r.id}:${r.build ? r.build.status : ''}:${r.draft ? r.draft.version : ''}`).join(',');
  }, { active: liveBuilds > 0, first: 10000, giveUpAfter: 0, signature: (x) => x });
  const isAdmin = ((window.AtlasWhoami && window.AtlasWhoami.role) || '').toLowerCase() === 'admin';
  const buildMissing = () => {
    setBusy(true);
    window.AtlasApi.getMissingIndustryBuilds()
      .then((m) => {
        const n = m.ready.length;
        if (!n) { window.pushToast({ type: 'info', title: 'Every active industry has a valuation, a draft or a build' }); return null; }
        const skipped = m.skipped.length ? ` ${m.skipped.length} skipped, no company with a website: ${m.skipped.map((x) => x.name).join(', ')}.` : '';
        return window.showConfirm({
          title: `Build ${n} valuation${n === 1 ? '' : 's'}?`,
          body: `${n} industr${n === 1 ? 'y has' : 'ies have'} no valuation, draft or build. Each build saves a draft for approval.${skipped}`,
          confirmLabel: `Build ${n}`,
        }).then((ok) => ok && window.AtlasApi.queueMissingIndustryBuilds().then((r) => {
          window.pushToast({ type: 'success', title: `${r.queued.length} build${r.queued.length === 1 ? '' : 's'} queued` });
          return load();
        }));
      })
      .catch((e) => window.pushToast({ type: 'error', title: 'Could not queue the builds', body: e.message }))
      .finally(() => { if (alive.current) setBusy(false); });
  };
  const queuedBuilds = (data || []).filter((r) => r.build && r.build.status === 'queued').length;
  const cancelQueued = () => {
    window.showConfirm({
      title: `Cancel ${queuedBuilds} queued build${queuedBuilds === 1 ? '' : 's'}?`,
      body: 'Builds already running keep going.',
      confirmLabel: 'Cancel queued', cancelLabel: 'Keep them', danger: true,
    }).then((ok) => {
      if (!ok) return;
      setBusy(true);
      window.AtlasApi.cancelQueuedIndustryBuilds()
        .then((r) => {
          window.pushToast({ type: 'success', title: `${r.canceled.length} build${r.canceled.length === 1 ? '' : 's'} canceled` });
          return load();
        })
        .catch((e) => window.pushToast({ type: 'error', title: 'Could not cancel the builds', body: e.message }))
        .finally(() => { if (alive.current) setBusy(false); });
    });
  };
  const rows = (data || []).filter((r) => !q.trim() || r.name.toLowerCase().includes(q.trim().toLowerCase()));
  const open = (slug) => { window.location.hash = `#/industries/${encodeURIComponent(slug)}`; };
  const withVal = (data || []).filter((r) => r.valuation).length;
  const status = (r) => (r.build ? (
    <window.Pill hue="blue" sz="sm" title={r.build.status === 'queued' ? 'Build queued' : (r.build.step_label || 'Building')}>
      {r.build.status === 'queued' ? 'Queued' : 'Building'}
    </window.Pill>
  ) : r.draft ? (
    <window.Pill hue="blue" sz="sm" title={`Draft v${r.draft.version} waiting for approval`}>Draft</window.Pill>
  ) : null);
  return (
    <IndScroller narrow={narrow}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap', marginBottom: 12 }}>
        {!(window.AtlasDevice && window.AtlasDevice.phone) && <h1 style={{ fontSize: 18, fontWeight: 650, margin: 0, color: 'var(--text)' }}>Industries</h1>}
        {data && <span style={indMuted}>{data.length} active · {withVal} with a valuation{liveBuilds ? ` · ${liveBuilds} building` : ''}</span>}
        <span style={{ flex: 1 }} />
        {isAdmin && queuedBuilds > 0 && (
          <window.Btn size="sm" variant="outline" disabled={busy} onClick={cancelQueued}
            title="Cancel every queued build; running ones keep going">
            Cancel queued ({queuedBuilds})
          </window.Btn>
        )}
        {isAdmin && data && (
          <window.Btn size="sm" variant="outline" disabled={busy} onClick={buildMissing}
            title="Build missing: a build for every active industry with no valuation, draft or build">
            {busy ? 'Checking…' : 'Build missing'}
          </window.Btn>
        )}
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter industries" aria-label="Filter industries"
          style={{ width: narrow ? '100%' : 220, height: 30, padding: '0 10px', fontSize: 12.5, borderRadius: 7, border: '1px solid var(--border)',
                   background: 'var(--surface)', color: 'var(--text)', boxSizing: 'border-box' }} />
      </div>
      {err && <div style={{ fontSize: 12.5, color: 'var(--rose-fg)' }}>Could not load industries: {err}</div>}
      {!data && !err && <window.SkeletonRows rows={10} />}
      {data && rows.length === 0 && <IndEmpty>{q ? `No industry matches “${q}”.` : 'No active industries.'}</IndEmpty>}
      {data && rows.length > 0 && narrow && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 10, background: 'var(--surface)' }}>
          {rows.map((r, i) => (
            <a key={r.id} href={`#/industries/${encodeURIComponent(r.slug)}`}
              style={{ display: 'block', padding: '10px 12px', borderTop: i ? '1px solid var(--border)' : 'none', textDecoration: 'none', color: 'var(--text)' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 600, flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{r.name}</span>
                {status(r)}
                {r.valuation && r.valuation.preliminary && <window.Pill hue="amber" sz="sm">Prelim</window.Pill>}
              </div>
              <div style={{ ...indMuted, marginTop: 3 }}>
                {r.valuation ? r.valuation.ranges_label : 'No valuation'}
                {r.closings ? ` · ${r.closings.n} closing${r.closings.n === 1 ? '' : 's'}` : ''}
                {` · ${r.lead ? r.lead.name : 'no coverage lead'}`}
              </div>
            </a>
          ))}
        </div>
      )}
      {data && rows.length > 0 && !narrow && (
        <div style={{ border: '1px solid var(--border)', borderRadius: 10, background: 'var(--surface)', overflow: 'hidden' }}>
          <IndTable minWidth={760} head={[['Industry'], ['Coverage'], ['Valuation'], ['Saved'], ['Our closings', 'right'], ['Live deals', 'right']]}>
            {rows.map((r) => (
              <tr key={r.id} onClick={(e) => { if (e.target.closest('a')) return; open(r.slug); }}
                style={{ borderTop: '1px solid var(--border)', cursor: 'pointer' }}
                onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-hover)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
                <td style={indTd}><a href={`#/industries/${encodeURIComponent(r.slug)}`} style={indLink}>{r.name}</a></td>
                <td style={indTd}>{r.lead ? <span style={{ color: r.lead.active ? 'var(--text-2)' : 'var(--amber-fg)' }}>{r.lead.name}</span>
                  : <span style={{ color: 'var(--amber-fg)' }}>No coverage lead</span>}</td>
                <td style={indTd}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    {r.valuation ? (
                      <>
                        <span style={{ fontVariantNumeric: 'tabular-nums', fontWeight: 500 }}>{r.valuation.ranges_label}</span>
                        {r.valuation.preliminary && <window.Pill hue="amber" sz="sm" title="Preliminary market estimate">Prelim</window.Pill>}
                      </>
                    ) : <span style={{ color: 'var(--text-4)' }} title={r.has_deck ? 'Only the Industry Overviews deck' : undefined}>{r.has_deck ? 'Deck only' : '—'}</span>}
                    {status(r)}
                  </span>
                </td>
                <td style={{ ...indTd, ...indMuted }}>{r.valuation ? `${indDate(r.valuation.created_at)}${r.valuation.run_by_name ? ` · ${r.valuation.run_by_name}` : ''}` : '—'}</td>
                <td style={{ ...indTd, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}
                  title={r.closings ? `${indX(r.closings.low)}–${indX(r.closings.high)} across ${r.closings.n}` : undefined}>
                  {r.closings ? <>{r.closings.n} <span style={{ color: 'var(--text-3)' }}>· {indX(r.closings.median)}</span></> : <span style={{ color: 'var(--text-4)' }}>—</span>}
                </td>
                <td style={{ ...indTd, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: r.live_deals ? 'var(--text)' : 'var(--text-4)' }}>{r.live_deals || '—'}</td>
              </tr>
            ))}
          </IndTable>
        </div>
      )}
    </IndScroller>
  );
};

// ── Valuation ────────────────────────────────────────────────────────────────

const IndRangeBand = ({ v, narrow }) => (
  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, minmax(0, 1fr))', gap: 8 }}>
    {IND_TIERS.map(([k, label]) => (
      <div key={k} style={{ border: '1px solid var(--border)', borderRadius: 8, background: k === 'high' ? 'var(--surface-2)' : 'var(--surface)', padding: narrow ? '8px 10px' : '10px 12px' }}>
        <div style={indCap}>{label}</div>
        <div style={{ fontSize: narrow ? 17 : 21, fontWeight: 650, color: 'var(--text)', fontVariantNumeric: 'tabular-nums', marginTop: 2, fontFamily: 'var(--font-display)' }}>
          {indTier(v.ranges[k])}
        </div>
      </div>
    ))}
  </div>
);

const IndTierTag = ({ tier }) => (tier ? (
  <span title={IND_TIER_TIP[tier]} aria-label={IND_TIER_TIP[tier]}
    style={{ display: 'inline-block', fontSize: 10, fontWeight: 600, color: tier === 1 ? 'var(--text)' : 'var(--text-3)', border: '1px solid var(--border)',
             borderRadius: 4, padding: '0 4px', lineHeight: '15px', marginLeft: 6, verticalAlign: 'middle', fontVariantNumeric: 'tabular-nums' }}>
    T{tier}
  </span>
) : null);

// The grid: category bands, one row per criterion. Narrow screens get one
// card per criterion with the three cells stacked.
const IndGrid = ({ criteria, narrow }) => {
  const cats = [];
  criteria.forEach((c) => { const g = cats.find((x) => x.name === c.category); if (g) g.rows.push(c); else cats.push({ name: c.category, rows: [c] }); });
  if (narrow) {
    return (
      <div>
        {cats.map((g) => (
          <div key={g.name}>
            {g.name && <div style={{ ...indCap, padding: '10px 12px 4px', background: 'var(--surface-2)', borderTop: '1px solid var(--border)' }}>{g.name}</div>}
            {g.rows.map((c) => (
              <div key={c.name} style={{ padding: '8px 12px', borderTop: '1px solid var(--border)' }}>
                <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{c.name}<IndTierTag tier={c.tier} /></div>
                {IND_TIERS.map(([k, label]) => (
                  <div key={k} style={{ display: 'grid', gridTemplateColumns: '58px 1fr', gap: 6, fontSize: 12, marginTop: 3 }}>
                    <span style={{ ...indCap, fontSize: 10, paddingTop: 1 }}>{label}</span>
                    <span style={{ color: 'var(--text-2)' }}>{c[k]}</span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        ))}
      </div>
    );
  }
  return (
    <div style={{ overflowX: 'auto' }}>
      <table style={{ width: '100%', minWidth: 760, borderCollapse: 'collapse', fontSize: 12.5, tableLayout: 'fixed' }}>
        <colgroup><col style={{ width: '22%' }} /><col style={{ width: '26%' }} /><col style={{ width: '26%' }} /><col style={{ width: '26%' }} /></colgroup>
        <thead>
          <tr>
            <th style={indTh}>Valuation characteristic</th>
            {IND_TIERS.map(([k, label]) => <th key={k} style={indTh}>{label}</th>)}
          </tr>
        </thead>
        {cats.map((g) => (
          <tbody key={g.name}>
            {g.name && <tr><td colSpan={4} style={{ ...indCap, padding: '8px 8px 4px', background: 'var(--surface-2)', borderTop: '1px solid var(--border)' }}>{g.name}</td></tr>}
            {g.rows.map((c) => (
              <tr key={c.name} style={{ borderTop: '1px solid var(--border)' }}>
                <td style={{ ...indTd, fontWeight: 600, color: 'var(--text)' }}>{c.name}<IndTierTag tier={c.tier} /></td>
                {IND_TIERS.map(([k]) => <td key={k} style={{ ...indTd, color: 'var(--text-2)', lineHeight: 1.45 }}>{c[k]}</td>)}
              </tr>
            ))}
          </tbody>
        ))}
      </table>
    </div>
  );
};

// A disclosure under the grid: research notes, buyers, sources, versions.
const IndFold = ({ id, title, count, defaultOpen = false, children }) => {
  const key = `atlas.industry.fold.${id}`;
  const [open, setOpen] = React.useState(() => { try { const v = localStorage.getItem(key); return v == null ? defaultOpen : v === '1'; } catch (_) { return defaultOpen; } });
  const toggle = () => { const next = !open; setOpen(next); try { localStorage.setItem(key, next ? '1' : '0'); } catch (_) {} };
  const Chevron = window.Icon && window.Icon.ChevronR;
  return (
    <div style={{ borderTop: '1px solid var(--border)' }}>
      <button type="button" aria-expanded={open} onClick={toggle}
        style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '9px 14px', border: 'none', background: 'transparent', cursor: 'pointer',
                 textAlign: 'left', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>
        <span aria-hidden style={{ display: 'inline-flex', color: 'var(--text-4)', transform: open ? 'rotate(90deg)' : 'none', transition: 'transform .15s ease' }}>
          {Chevron ? <Chevron size={11} /> : '›'}
        </span>
        {title}
        {count != null && <span style={{ fontSize: 11.5, color: 'var(--text-4)', fontWeight: 500, fontVariantNumeric: 'tabular-nums' }}>{count}</span>}
      </button>
      {open && <div style={{ padding: '0 14px 12px' }}>{children}</div>}
    </div>
  );
};

const IndNotes = ({ notes }) => {
  const have = IND_NOTES.filter(([k]) => notes && notes[k]);
  if (!have.length) return <div style={indMuted}>No research notes.</div>;
  return (
    <div style={{ display: 'grid', gap: 12 }}>
      {have.map(([k, label]) => (
        <div key={k}>
          <div style={{ ...indCap, marginBottom: 3 }}>{label}</div>
          <div style={{ fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.5 }}>
            {window.Markdown ? <window.Markdown strict>{notes[k]}</window.Markdown> : <span style={{ whiteSpace: 'pre-wrap' }}>{notes[k]}</span>}
          </div>
        </div>
      ))}
    </div>
  );
};

const IndBuyers = ({ buyers, narrow }) => {
  const groups = [['strategic', 'Strategic'], ['sponsor', 'Sponsor']];
  return (
    <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr' : '1fr 1fr', gap: 14 }}>
      {groups.map(([k, label]) => {
        const rows = (buyers || []).filter((b) => b.type === k);
        return (
          <div key={k}>
            <div style={{ ...indCap, marginBottom: 4 }}>{label} · {rows.length}</div>
            {rows.length === 0 && <div style={indMuted}>None named.</div>}
            {rows.map((b) => (
              <div key={b.name} style={{ fontSize: 12.5, padding: '3px 0', color: 'var(--text)' }}>
                {b.website ? <a href={b.website} target="_blank" rel="noopener noreferrer" style={indLink}>{b.name}</a> : <span style={{ fontWeight: 500 }}>{b.name}</span>}
                {b.why && <span style={{ color: 'var(--text-3)' }}> — {b.why}</span>}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

const IndSources = ({ sources }) => (
  <ol style={{ margin: 0, paddingLeft: 18, fontSize: 12.5, color: 'var(--text-2)', lineHeight: 1.6 }}>
    {(sources || []).map((s, i) => (
      <li key={i}>{s.url ? <a href={s.url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-text)' }}>{s.title || indHost(s.url)}</a> : s.title}</li>
    ))}
  </ol>
);

const IndVersions = ({ versions, shown, onShow, canEdit, onMakeCurrent, busy }) => (
  <div>
    {versions.map((v) => {
      const viewing = shown === v.version;
      return (
        <div key={v.id} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '6px 0', borderTop: '1px solid var(--border)', fontSize: 12.5 }}>
          <button type="button" onClick={() => onShow(v.version)} aria-pressed={viewing}
            style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5,
                     fontWeight: viewing ? 650 : 500, color: viewing ? 'var(--text)' : 'var(--accent-text)', textAlign: 'left' }}>
            v{v.version} · {indDate(v.created_at)}
          </button>
          {v.is_current && <window.Pill hue="green" sz="sm">Current</window.Pill>}
          {v.status === 'draft' && <window.Pill hue="blue" sz="sm" title="Built by Atlas, waiting for approval">Draft</window.Pill>}
          {v.status === 'discarded' && <window.Pill hue="slate" sz="sm" title={v.reviewed_by_name ? `Discarded by ${v.reviewed_by_name}` : 'Discarded'}>Discarded</window.Pill>}
          {v.preliminary && <window.Pill hue="amber" sz="sm">Prelim</window.Pill>}
          <span style={{ color: 'var(--text-3)', fontVariantNumeric: 'tabular-nums' }}>{v.ranges_label}</span>
          <span style={{ color: 'var(--text-4)' }}>
            {v.via === 'builder' ? `Atlas${v.run_by_name ? ` for ${v.run_by_name}` : ''}` : (v.run_by_name || 'Unknown')}
            {v.via === 'builder' && v.status === 'approved' && v.reviewed_by_name ? ` · approved by ${v.reviewed_by_name}` : ''}
            {' · '}{indHost(v.representative_url)} · {v.criteria} criteria
          </span>
          {v.note && v.via !== 'builder' && <span style={{ color: 'var(--text-3)', flexBasis: '100%' }}>{v.note}</span>}
          {canEdit && !v.is_current && viewing && v.status === 'approved' && (
            <window.Btn size="sm" variant="outline" disabled={busy} onClick={() => onMakeCurrent(v.version)}>{busy ? 'Saving…' : 'Make current'}</window.Btn>
          )}
        </div>
      );
    })}
  </div>
);

// What {{multiple_range}} reads until a valuation is saved.
const IndDeck = ({ deck, narrow }) => {
  const t = deck.tiers || {};
  return (
    <div style={{ marginTop: 4 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
        <window.Pill hue="slate" sz="sm">Industry Overviews deck</window.Pill>
        <span style={{ fontSize: 12.5, color: 'var(--text-2)', fontVariantNumeric: 'tabular-nums' }}>
          {t.low || '—'} / {t.average || '—'} / {t.high || '—'} {deck.multiple_basis === 'revenue' ? 'revenue' : 'EBITDA'}
        </span>
        {window.HelpTip && <window.HelpTip title="Fallback" text="Email fields and the Email Copilot read the deck's rubric for this industry until a valuation is saved." />}
      </div>
      {(deck.rows || []).length > 0 && (
        <IndGrid narrow={narrow} criteria={deck.rows.map((r) => ({ category: '', name: r.factor, low: r.low || '—', average: r.average || '—', high: r.high || '—' }))} />
      )}
    </div>
  );
};

// ── Build valuation ──────────────────────────────────────────────────────────

const IndStepper = ({ step, status }) => {
  const at = status === 'queued' ? -1 : IND_BUILD_STEPS.findIndex(([k]) => k === step);
  return (
    <ol aria-label="Build progress" style={{ display: 'flex', flexWrap: 'wrap', gap: 4, listStyle: 'none', margin: '8px 0 0', padding: 0 }}>
      {IND_BUILD_STEPS.map(([k, label], i) => {
        const done = at > i;
        const now = at === i;
        return (
          <li key={k} aria-current={now ? 'step' : undefined} aria-label={`${label}${done ? ', done' : now ? ', in progress' : ''}`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11.5, padding: '2px 8px', borderRadius: 999,
                     border: '1px solid var(--border)', background: now ? 'var(--surface)' : 'transparent',
                     color: done ? 'var(--text-2)' : now ? 'var(--text)' : 'var(--text-4)', fontWeight: now ? 600 : 500 }}>
            <span aria-hidden data-ind-pulse={now ? '' : undefined}
              style={{ width: 6, height: 6, borderRadius: 999, flexShrink: 0,
                       background: done ? 'var(--green-fg)' : now ? 'var(--accent)' : 'var(--border-strong, var(--border))',
                       animation: now ? 'atlasIndustryPulse 1.4s ease-in-out infinite' : 'none' }} />
            {label}
          </li>
        );
      })}
    </ol>
  );
};

// The build's trail: what it searched, read and decided (window.RunSteps,
// the research wand's renderer). Live, it follows the newest step.
const IndTrail = ({ steps, live = false }) => {
  const box = React.useRef(null);
  const n = (steps || []).length;
  React.useEffect(() => { if (live && box.current) box.current.scrollTop = box.current.scrollHeight; }, [live, n]);
  if (!n) return null;
  const mapped = steps.map((s) => ({ ...s, kind: IND_STEP_KIND[s.kind] || s.kind }));
  const Steps = window.RunSteps;
  return (
    <div ref={box} tabIndex={0} aria-label="Build steps" style={{ maxHeight: 220, overflowY: 'auto', fontSize: 12, marginTop: 8, paddingRight: 4 }}>
      {Steps ? <Steps steps={mapped} live={live} /> : mapped.map((s, i) => <div key={i}>{s.label}</div>)}
    </div>
  );
};

const IndRepLink = ({ build }) => (
  <a href={build.representative_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-text)' }}>
    {build.representative ? build.representative.name : indHost(build.representative_url)}
  </a>
);

// Queued / running / failed / "same grid" — what the newest build says.
// A finished build that produced a draft shows as the draft bar instead.
const IndBuildPanel = ({ build, page, slug, onBuild, onReload }) => {
  const [busy, setBusy] = React.useState(false);
  const [trail, setTrail] = React.useState(false);
  React.useEffect(indPulseStyle, []);
  if (!build) return null;
  const active = indActive(build);
  const newest = page.versions.length ? new Date(page.versions[0].created_at).getTime() : 0;
  const stale = !active && build.finished_at && new Date(build.finished_at).getTime() < newest;
  if (!active && (stale || build.status === 'canceled' || build.outcome === 'draft')) return null;
  const cancel = () => {
    window.showConfirm({ title: 'Cancel this build?', confirmLabel: 'Cancel build', cancelLabel: 'Keep building', danger: true }).then((ok) => {
      if (!ok) return;
      setBusy(true);
      window.AtlasApi.cancelIndustryBuild(slug, build.id)
        .then(() => onReload())
        .catch((e) => window.pushToast({ type: 'error', title: 'Could not cancel the build', body: e.message }))
        .finally(() => setBusy(false));
    });
  };
  const bar = { padding: '10px 14px', borderBottom: '1px solid var(--border)' };
  const row = { display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' };
  if (active) {
    return (
      <div role="status" aria-live="polite" style={{ ...bar, background: 'var(--surface-2)' }}>
        <div style={row}>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>{build.status === 'queued' ? 'Build queued' : 'Building valuation'}</span>
          <span style={indMuted}>
            from <IndRepLink build={build} />{build.requested_by_name ? ` · ${build.requested_by_name}` : ''} · {indAgo(build.started_at || build.created_at)}
          </span>
          {page.can_edit && (
            <span style={indActions}><window.Btn size="sm" variant="outline" disabled={busy} onClick={cancel}>{busy ? 'Canceling…' : 'Cancel'}</window.Btn></span>
          )}
        </div>
        <IndStepper step={build.step} status={build.status} />
        <IndTrail steps={build.steps} live />
      </div>
    );
  }
  if (build.status === 'failed') {
    return (
      <div role="alert" style={{ ...bar, background: 'var(--rose-bg)', color: 'var(--rose-fg)' }}>
        <div style={row}>
          <span style={{ fontSize: 13, fontWeight: 600 }}>Build failed</span>
          <span style={{ fontSize: 12 }}>{indDate(build.finished_at)} · from <IndRepLink build={build} /></span>
          <span style={indActions}>
            {build.steps.length > 0 && (
              <button type="button" aria-expanded={trail} onClick={() => setTrail((t) => !t)} style={indTextBtn}>
                {trail ? 'Hide steps' : `Steps (${build.steps.length})`}
              </button>
            )}
            {page.can_edit && <window.Btn size="sm" variant="outline" onClick={onBuild}>Build again</window.Btn>}
          </span>
        </div>
        <div style={{ fontSize: 12.5, marginTop: 4, wordBreak: 'break-word', lineHeight: 1.45 }}>{build.error}</div>
        {trail && <IndTrail steps={build.steps} />}
      </div>
    );
  }
  if (build.status === 'done' && build.outcome === 'same') {
    return <div style={{ ...bar, ...indMuted }}>The {indDate(build.finished_at)} build wrote the same grid as the current version.</div>;
  }
  if (build.status === 'done' && build.outcome === 'same_draft' && page.can_edit) {
    const v = build.valuation_version ? ` v${build.valuation_version}` : '';
    return <div style={{ ...bar, ...indMuted }}>The {indDate(build.finished_at)} build wrote the same grid as draft{v}, waiting for approval.</div>;
  }
  return null;
};

// A draft from Atlas: what it changes, Review, Approve or Discard.
const IndDraftBar = ({ draft, cur, viewing, onView, page, slug, onReload }) => {
  const [busy, setBusy] = React.useState(null);
  const act = (kind) => {
    const approve = kind === 'approve';
    window.showConfirm(approve ? {
      title: `Approve draft v${draft.version}?`,
      body: `${cur ? `It replaces v${cur.version} as the current valuation.` : 'It becomes the current valuation.'} Email fields and the Email Copilot read the current version.`,
      confirmLabel: 'Approve',
    } : {
      title: `Discard draft v${draft.version}?`, body: 'It stays in the version history.', confirmLabel: 'Discard', danger: true,
    }).then((ok) => {
      if (!ok) return;
      setBusy(kind);
      (approve ? window.AtlasApi.approveIndustryVersion : window.AtlasApi.discardIndustryVersion)(slug, draft.version)
        .then(() => { window.pushToast({ type: 'success', title: approve ? `v${draft.version} is current` : `Draft v${draft.version} discarded` }); onReload(); })
        .catch((e) => window.pushToast({ type: 'error', title: approve ? 'Could not approve the draft' : 'Could not discard the draft', body: e.message }))
        .finally(() => setBusy(null));
    });
  };
  const changes = cur ? (draft.changes || []) : [];
  return (
    <div role="status" style={{ padding: '10px 14px', borderBottom: '1px solid var(--border)', background: 'var(--accent-soft)', color: 'var(--accent-text)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>Draft v{draft.version} waiting for approval</span>
        <span style={{ fontSize: 12 }}>
          Atlas · {indDate(draft.created_at)} · from{' '}
          <a href={draft.representative_url} target="_blank" rel="noopener noreferrer" style={{ color: 'inherit' }}>{indHost(draft.representative_url)}</a>
          {draft.run_by_name ? ` · for ${draft.run_by_name}` : ''}
        </span>
        <span style={indActions}>
          {(cur || !viewing) && (
            <button type="button" aria-pressed={viewing} onClick={() => onView(viewing ? cur.version : draft.version)} style={indTextBtn}>
              {viewing ? 'Back to current' : 'Review draft'}
            </button>
          )}
          {page.can_edit ? (
            <>
              <window.Btn size="sm" variant="outline" disabled={!!busy} onClick={() => act('discard')}>{busy === 'discard' ? 'Discarding…' : 'Discard'}</window.Btn>
              <window.Btn size="sm" variant="primary" disabled={!!busy} onClick={() => act('approve')}>{busy === 'approve' ? 'Approving…' : 'Approve'}</window.Btn>
            </>
          ) : window.HelpTip && <window.HelpTip title="Approval" text="Someone with Manage industries approves a draft before email fields and the Email Copilot read it." />}
        </span>
      </div>
      {changes.length > 0 && (
        <ul aria-label={`What v${draft.version} changes`} style={{ margin: '6px 0 0', paddingLeft: 18, fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-2)' }}>
          {changes.map((c, i) => <li key={i}>{c}</li>)}
        </ul>
      )}
    </div>
  );
};

const IndRadio = ({ name, checked, onChange, children }) => (
  <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 10px', border: `1px solid ${checked ? 'var(--border-strong, var(--text-4))' : 'var(--border)'}`,
                  borderRadius: 8, cursor: 'pointer', background: checked ? 'var(--surface-2)' : 'var(--surface)' }}>
    <input type="radio" name={name} checked={checked} onChange={onChange} style={{ marginTop: 3, accentColor: 'var(--accent)' }} />
    <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: 'var(--text)' }}>{children}</span>
  </label>
);

// Build valuation: the representative company Atlas suggests, or another.
const IndBuildDrawer = ({ open, onClose, page, slug, onStarted }) => {
  const ind = page.industry;
  const [sug, setSug] = React.useState(undefined);       // undefined = loading, null = none
  const [mode, setMode] = React.useState('suggested');   // suggested | company | url
  const [pick, setPick] = React.useState('');
  const [url, setUrl] = React.useState('');
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState(null);
  React.useEffect(() => {
    if (!open) return undefined;
    let alive = true;
    setSug(undefined); setMode('suggested'); setPick(''); setUrl(''); setErr(null);
    window.AtlasApi.getIndustryBuildSuggestion(slug)
      .then((r) => { if (!alive) return; setSug(r.suggestion || null); if (!r.suggestion) setMode('company'); })
      .catch(() => { if (alive) { setSug(null); setMode('company'); } });
    return () => { alive = false; };
  }, [open, slug]);
  const loadCompanies = React.useCallback((q) => (
    window.AtlasApi.listCompanies(q ? { q, my_team: false, limit: 20 } : { industry_id: ind.id, my_team: false, limit: 30 })
  ).then((r) => (r.items || []).map((c) => ({ value: c.id, label: c.name, hint: c.website ? indHost(c.website) : 'no website' }))), [ind.id]);
  const body = mode === 'suggested' && sug ? { company_id: sug.company_id }
    : mode === 'company' && pick ? { company_id: pick }
    : mode === 'url' && url.trim() ? { url: url.trim() } : null;
  const start = () => {
    if (!body) return;
    setBusy(true); setErr(null);
    window.AtlasApi.startIndustryBuild(slug, body)
      .then((r) => { window.pushToast({ type: 'success', title: r.already ? 'A build is already running' : 'Build queued' }); onStarted(r.build); onClose(); })
      .catch((e) => setErr(e.message || String(e)))
      .finally(() => setBusy(false));
  };
  const footer = (
    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
      <window.Btn variant="outline" onClick={onClose}>Cancel</window.Btn>
      <window.Btn variant="primary" disabled={!body || busy} onClick={start}>{busy ? 'Queuing…' : 'Build'}</window.Btn>
    </div>
  );
  const why = sug && IND_WHY[sug.why];
  return (
    <window.Drawer open={open} onClose={onClose} width={440} title="Build valuation" subtitle={ind.name} footer={footer}>
      <div style={{ display: 'grid', gap: 8 }}>
        <div style={{ ...indCap, display: 'flex', alignItems: 'center', gap: 6 }}>
          Representative company
          {window.HelpTip && <window.HelpTip title="Representative company" text="Atlas reads its website to pin down the sub-industry and the business model. The grid is for the whole trade; nothing about this company is kept." />}
        </div>
        {sug === undefined && <window.Skeleton w={260} h={44} />}
        {sug && (
          <IndRadio name="ind-rep" checked={mode === 'suggested'} onChange={() => setMode('suggested')}>
            <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 600 }}>{sug.name}</span>
              {why && <window.Pill hue={sug.why === 'took_to_market' ? 'green' : 'slate'} sz="sm" title={why[1]}>{why[0]}</window.Pill>}
            </span>
            <a href={sug.website} target="_blank" rel="noopener noreferrer" onClick={(e) => e.stopPropagation()} style={{ fontSize: 12, color: 'var(--accent-text)' }}>{indHost(sug.website)}</a>
          </IndRadio>
        )}
        {sug === null && <div style={indMuted}>No company in {ind.name} has a website yet.</div>}
        {sug !== undefined && (
          <>
            <IndRadio name="ind-rep" checked={mode === 'company'} onChange={() => setMode('company')}>Another company</IndRadio>
            {mode === 'company' && (
              <window.Combobox value={pick} onChange={(e) => setPick(e.target.value)} searchable searchAlways
                loadOptions={loadCompanies} placeholder="Search companies…" />
            )}
            <IndRadio name="ind-rep" checked={mode === 'url'} onChange={() => setMode('url')}>A website</IndRadio>
            {mode === 'url' && (
              <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" aria-label="Website to build from" autoFocus
                onKeyDown={(e) => { if (e.key === 'Enter') start(); }}
                style={{ height: 32, padding: '0 10px', fontSize: 12.5, borderRadius: 7, border: '1px solid var(--border)', background: 'var(--surface)',
                         color: 'var(--text)', boxSizing: 'border-box', width: '100%' }} />
            )}
          </>
        )}
        {err && <div role="alert" style={{ fontSize: 12.5, color: 'var(--rose-fg)', marginTop: 4 }}>{err}</div>}
      </div>
    </window.Drawer>
  );
};

const IndValuation = ({ page, slug, narrow, onReload, build, onBuild }) => {
  const cur = page.valuation;
  const draft = page.draft;
  // With no current version, the draft is all there is to read.
  const initial = cur ? cur.version : draft ? draft.version : null;
  const [shown, setShown] = React.useState(initial);
  const [older, setOlder] = React.useState(cur ? null : draft);
  const [busy, setBusy] = React.useState(false);
  React.useEffect(() => { setShown(initial); setOlder(cur ? null : draft); }, [cur && cur.id, draft && draft.id]);   // eslint-disable-line react-hooks/exhaustive-deps
  // Switching versions from the list far below brings the grid back into view.
  const top = () => { const el = document.getElementById('industry-valuation'); if (el) el.scrollIntoView({ block: 'start', behavior: 'smooth' }); };
  const show = (version) => {
    if (cur && version === cur.version) { setShown(version); setOlder(null); top(); return; }
    setShown(version);
    top();
    if (draft && version === draft.version) { setOlder(draft); return; }
    window.AtlasApi.getIndustryVersion(slug, version).then(setOlder)
      .catch((e) => { window.pushToast({ type: 'error', title: 'Could not open that version', body: e.message }); setShown(initial); });
  };
  const makeCurrent = (version) => {
    window.showConfirm({
      title: `Make v${version} current?`,
      body: 'Email fields and the Email Copilot read the current version.',
      confirmLabel: 'Make current',
    }).then((ok) => {
      if (!ok) return;
      setBusy(true);
      window.AtlasApi.makeIndustryVersionCurrent(slug, version)
        .then(() => { window.pushToast({ type: 'success', title: `v${version} is current` }); onReload(); })
        .catch((e) => window.pushToast({ type: 'error', title: 'Could not change the version', body: e.message }))
        .finally(() => setBusy(false));
    });
  };
  const head = (
    <>
      <IndBuildPanel build={build} page={page} slug={slug} onBuild={onBuild} onReload={onReload} />
      {draft && <IndDraftBar draft={draft} cur={cur} viewing={shown === draft.version} onView={show} page={page} slug={slug} onReload={onReload} />}
    </>
  );
  const v = older && older.version === shown ? older : cur;
  if (!v) {
    return (
      <div>
        {head}
        {!indActive(build) && (
          <IndEmpty action={window.HelpTip && <window.HelpTip title="How a valuation gets here" text="Build valuation runs the IndustryPro valuation method in Atlas and saves a draft for approval. The valuation-profile skill in Claude can also save one through the Atlas connector." />}>
            No valuation yet.
          </IndEmpty>
        )}
        {page.deck && <div style={{ padding: '0 14px 12px' }}><IndDeck deck={page.deck} narrow={narrow} /></div>}
      </div>
    );
  }
  const isDraft = v.status === 'draft';
  const loadingOlder = shown !== (older ? older.version : cur && cur.version);
  const built = v.via === 'builder';
  return (
    <div>
      {head}
      {v !== cur && !isDraft && (
        <div role="status" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', padding: '8px 14px', background: 'var(--amber-bg)', color: 'var(--amber-fg)', fontSize: 12.5 }}>
          <span>Viewing v{v.version} from {indDate(v.created_at)}{v.status === 'discarded' ? ', a discarded draft' : ', not the current version'}.</span>
          {cur && <button type="button" onClick={() => show(cur.version)} style={indTextBtn}>Back to current</button>}
          {page.can_edit && v.status === 'approved' && <window.Btn size="sm" variant="outline" disabled={busy} onClick={() => makeCurrent(v.version)}>{busy ? 'Saving…' : 'Make current'}</window.Btn>}
        </div>
      )}
      <div style={{ padding: '12px 14px', opacity: loadingOlder ? 0.5 : 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: 8 }}>
          <span style={indCap}>{indMetric(v)} multiple range</span>
          {isDraft && <window.Pill hue="blue" sz="sm" title="Built by Atlas, waiting for approval">Draft</window.Pill>}
          {v.preliminary && <window.Pill hue="amber" sz="sm" title="The evidence behind the ranges is thin">Preliminary market estimate</window.Pill>}
        </div>
        <IndRangeBand v={v} narrow={narrow} />
        <div style={{ ...indMuted, marginTop: 8, lineHeight: 1.5 }}>
          {v.sector && <span style={{ color: 'var(--text-2)', fontWeight: 500, display: narrow ? 'block' : 'inline' }}>{v.sector}{narrow ? '' : ' · '}</span>}
          v{v.version} · {built ? `Atlas${v.run_by_name ? ` for ${v.run_by_name}` : ''}` : (v.run_by_name || 'Unknown')} · {indDate(v.created_at)} · {built ? 'built from' : 'run on'}{' '}
          <a href={v.representative_url} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--accent-text)' }}>{indHost(v.representative_url)}</a>
          {built && v.status === 'approved' && v.reviewed_by_name ? ` · approved by ${v.reviewed_by_name}` : ''}
        </div>
      </div>
      <div style={{ borderTop: '1px solid var(--border)' }}><IndGrid criteria={v.criteria || []} narrow={narrow} /></div>
      <IndFold id="notes" title="Research notes"><IndNotes notes={v.research_notes} /></IndFold>
      <IndFold id="buyers" title="Buyers" count={(v.buyers || []).length}><IndBuyers buyers={v.buyers} narrow={narrow} /></IndFold>
      <IndFold id="sources" title="Sources" count={(v.sources || []).length}><IndSources sources={v.sources} /></IndFold>
      <IndFold id="versions" title="Versions" count={page.versions.length}>
        <IndVersions versions={page.versions} shown={shown} onShow={show} canEdit={page.can_edit} onMakeCurrent={makeCurrent} busy={busy} />
      </IndFold>
    </div>
  );
};

// ── Our numbers ──────────────────────────────────────────────────────────────

const IndStat = ({ label, value, sub, title }) => (
  <div title={title} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: '9px 12px', background: 'var(--surface)', minWidth: 0 }}>
    <div style={indCap}>{label}</div>
    <div style={{ fontSize: 18, fontWeight: 650, color: value == null ? 'var(--text-4)' : 'var(--text)', fontVariantNumeric: 'tabular-nums', marginTop: 2 }}>{value == null ? '—' : value}</div>
    {sub && <div style={{ ...indMuted, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{sub}</div>}
  </div>
);

const IndSub = ({ title, count, children }) => (
  <div style={{ borderTop: '1px solid var(--border)', paddingTop: 10, marginTop: 12 }}>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, padding: '0 14px 4px' }}>
      <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>{title}</span>
      {count != null && <span style={{ fontSize: 11.5, color: 'var(--text-4)', fontVariantNumeric: 'tabular-nums' }}>{count}</span>}
    </div>
    <div style={{ padding: '0 6px' }}>{children}</div>
  </div>
);

const IndNumbers = ({ page, narrow }) => {
  const n = page.our_numbers;
  const s = n.summary;
  const bp = n.busiest_process;
  return (
    <div style={{ padding: '12px 0 12px' }}>
      <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr 1fr' : 'repeat(4, minmax(0, 1fr))', gap: 8, padding: '0 14px' }}>
        <IndStat label="Our closings" value={s ? s.n : 0} sub={s ? `median ${indX(s.median)}` : 'none yet'} />
        <IndStat label="Closing range" value={s ? `${indX(s.low)}–${indX(s.high)}` : null} sub={s && s.n > 2 ? `middle half ${indX(s.p25)}–${indX(s.p75)}` : undefined}
          title="EV / EBITDA of our closings in this industry" />
        <IndStat label="Busiest process" value={bp ? `${bp.value} NDA${bp.value === 1 ? '' : 's'}` : null} sub={bp ? bp.label.replace(/^Our process · /, '') : 'none in the last year'} title={bp ? bp.detail : undefined} />
        <IndStat label="Live deals" value={n.live_total} sub={n.live_sellside ? `${n.live_sellside} in a sell-side process` : undefined} />
      </div>
      <IndSub title="Closings" count={n.closings.length}>
        {n.closings.length === 0 ? <IndEmpty>No closings in this industry yet.</IndEmpty> : (
          <IndTable minWidth={640} head={[['Company'], ['Closed'], ['EBITDA', 'right'], ['EV', 'right'], ['Multiple', 'right'], ['Buyer'], ['Dealmaker']]}>
            {n.closings.map((c, i) => (
              <tr key={i} style={{ borderTop: '1px solid var(--border)' }}>
                <td style={indTd}>{c.engagement_id ? <a href={`#/deals/${c.engagement_id}`} style={indLink}>{c.company}</a> : c.company}</td>
                <td style={{ ...indTd, ...indMuted }}>{indDate(c.closed_on)}</td>
                <td style={{ ...indTd, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{indMoney(c.ebitda_usd)}</td>
                <td style={{ ...indTd, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{indMoney(c.ev_usd)}</td>
                <td style={{ ...indTd, textAlign: 'right', fontVariantNumeric: 'tabular-nums', fontWeight: 600 }}>{indX(c.multiple)}</td>
                <td style={{ ...indTd, color: 'var(--text-2)' }}>{c.buyer || '—'}{c.buyer_type ? <span style={{ color: 'var(--text-4)' }}> · {c.buyer_type}</span> : null}</td>
                <td style={{ ...indTd, ...indMuted }}>{c.dealmaker || '—'}</td>
              </tr>
            ))}
          </IndTable>
        )}
      </IndSub>
      <IndSub title="Live deals" count={n.live_total}>
        {n.live_deals.length === 0 ? <IndEmpty>No live deals in this industry.</IndEmpty> : (
          <>
            <IndTable minWidth={600} head={[['Deal'], ['Stage'], ['Dealmaker'], ['EBITDA', 'right'], ['Last touch']]}>
              {n.live_deals.map((d) => (
                <tr key={d.id} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={indTd}><a href={`#/deals/${d.id}`} style={indLink}>{d.name}</a></td>
                  <td style={{ ...indTd, color: 'var(--text-2)' }}>{d.stage}</td>
                  <td style={{ ...indTd, ...indMuted }}>{d.dealmaker || '—'}</td>
                  <td style={{ ...indTd, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{indMoney(d.ebitda)}</td>
                  <td style={{ ...indTd, ...indMuted }}>{d.last_touch ? window.relativeTime(d.last_touch) : '—'}</td>
                </tr>
              ))}
            </IndTable>
            {n.live_total > n.live_deals.length && <div style={{ ...indMuted, padding: '6px 8px' }}>Furthest along {n.live_deals.length} of {n.live_total}.</div>}
          </>
        )}
      </IndSub>
      <IndSub title={`Buyers active on our deals · ${n.buyer_months} months`} count={n.buyers.length}>
        {n.buyers.length === 0 ? <IndEmpty>No buyer signed an NDA or bid on our deals here in that time.</IndEmpty> : (
          <IndTable minWidth={560} head={[['Buyer'], ['Deals', 'right'], ['NDAs', 'right'], ['IOIs', 'right'], ['LOIs', 'right'], ['Last']]}>
            {n.buyers.map((b) => (
              <tr key={b.company_id} style={{ borderTop: '1px solid var(--border)' }}>
                <td style={indTd}>
                  <a href={`#/companies/${b.company_id}`} style={indLink}>{b.name}</a>
                  {b.closed && <span style={{ marginLeft: 6 }}><window.Pill hue="green" sz="sm" title="Closed one of our deals in this industry">Closed with us</window.Pill></span>}
                </td>
                {['deals', 'ndas', 'iois', 'lois'].map((k) => (
                  <td key={k} style={{ ...indTd, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: b[k] ? 'var(--text)' : 'var(--text-4)' }}>{b[k] || '—'}</td>
                ))}
                <td style={{ ...indTd, ...indMuted }}>{indDate(b.last_on)}</td>
              </tr>
            ))}
          </IndTable>
        )}
      </IndSub>
    </div>
  );
};

// ── Market (lazy: Signals › Market's data, this trade only) ──────────────────

const IndMarket = ({ slug, narrow }) => {
  const [m, setM] = React.useState(null);
  const [err, setErr] = React.useState(null);
  React.useEffect(() => {
    let alive = true;
    window.AtlasApi.getIndustryMarket(slug).then((r) => { if (alive) setM(r); }).catch((e) => { if (alive) setErr(e.message || String(e)); });
    return () => { alive = false; };
  }, [slug]);
  if (err) return <IndEmpty>Could not load the market: {err}</IndEmpty>;
  if (!m) return <div style={{ padding: 14 }}><window.SkeletonRows rows={4} /></div>;
  const t = m.totals || {};
  return (
    <div style={{ padding: '12px 0' }}>
      {!t.n ? <IndEmpty>No sales recorded in this trade in the last {m.months} months.</IndEmpty> : <>
      <div style={{ display: 'grid', gridTemplateColumns: narrow ? '1fr 1fr' : 'repeat(3, minmax(0, 1fr))', gap: 8, padding: '0 14px' }}>
        <IndStat label={`Sales · ${m.months} months`} value={t.n || 0} sub={t.n && t.dated < t.n ? `${t.n - t.dated} undated` : undefined} />
        <IndStat label="Acquirers" value={t.acquirers || 0} sub={t.roster_acquirers ? `${t.roster_acquirers} on our roster` : undefined} />
        <IndStat label="Top states" value={(m.by_state || []).length ? m.by_state.slice(0, 3).map((s) => s.state).join(', ') : null} />
      </div>
      <IndSub title="Recent sales" count={m.sales.length}>
        {m.sales.length === 0 ? <IndEmpty>Only undated sales in that time.</IndEmpty> : (
          <IndTable minWidth={560} head={[['Sold'], ['Where'], ['Acquirer'], ['When']]}>
            {m.sales.map((s) => (
              <tr key={s.id} style={{ borderTop: '1px solid var(--border)' }}>
                <td style={indTd}>
                  {s.company_id ? <a href={`#/companies/${s.company_id}`} style={indLink}>{s.target_name}</a> : <span style={{ fontWeight: 500 }}>{s.target_name}</span>}
                  {s.source_url && <a href={s.source_url} target="_blank" rel="noopener noreferrer" aria-label="Source" title="Source"
                    style={{ marginLeft: 6, color: 'var(--text-4)', verticalAlign: 'middle' }}>{window.Icon && window.Icon.External ? <window.Icon.External size={11} /> : '↗'}</a>}
                </td>
                <td style={{ ...indTd, ...indMuted }}>{[s.target_city, s.state].filter(Boolean).join(', ') || '—'}</td>
                <td style={{ ...indTd, color: 'var(--text-2)' }}>{s.acquirer_name || 'Undisclosed'}</td>
                <td style={{ ...indTd, ...indMuted }}>{s.sold_on ? indDate(s.sold_on) : 'Undated'}</td>
              </tr>
            ))}
          </IndTable>
        )}
      </IndSub>
      {(m.acquirers || []).length > 0 && (
        <IndSub title="Most active acquirers" count={m.acquirers.length}>
          <IndTable minWidth={420} head={[['Acquirer'], ['Deals', 'right'], ['Last']]}>
            {m.acquirers.map((a) => (
              <tr key={a.key} style={{ borderTop: '1px solid var(--border)' }}>
                <td style={indTd}>
                  {a.company_id ? <a href={`#/companies/${a.company_id}`} style={indLink}>{a.name}</a> : <span style={{ fontWeight: 500 }}>{a.name}</span>}
                  {a.status === 'roster' && <span style={{ marginLeft: 6 }}><window.Pill hue="green" sz="sm">On our roster</window.Pill></span>}
                </td>
                <td style={{ ...indTd, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{a.deals}</td>
                <td style={{ ...indTd, ...indMuted }}>{a.last_sold_on ? indDate(a.last_sold_on) : '—'}</td>
              </tr>
            ))}
          </IndTable>
        </IndSub>
      )}
      </>}
      <div style={{ marginTop: 12 }}>
        <IndFold id="gfdata" title={<span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>GF Data <window.Pill hue="violet" sz="sm" title="Licensed for our own analysis — never quoted in emails">Internal</window.Pill></span>}
          count={(m.gfdata || []).length}>
          {(m.gfdata || []).length === 0 ? (
            <div style={indMuted}>{(m.naics_codes || []).length ? `No GF Data for NAICS ${m.naics_codes.join(', ')}.` : 'No NAICS codes on this industry.'}</div>
          ) : (
            <IndTable minWidth={460} head={[['NAICS'], ['Period · size'], ['EV / EBITDA', 'right']]}>
              {m.gfdata.map((f) => (
                <tr key={f.key} style={{ borderTop: '1px solid var(--border)' }}>
                  <td style={{ ...indTd, color: 'var(--text-2)' }}>{f.label.replace(/^GF Data · /, '')}</td>
                  <td style={{ ...indTd, ...indMuted }}>{f.detail.split(' · ').slice(0, 2).join(' · ')}</td>
                  <td style={{ ...indTd, textAlign: 'right', fontVariantNumeric: 'tabular-nums' }} title={f.detail}>{indX(f.mean != null ? f.mean : f.median)}</td>
                </tr>
              ))}
            </IndTable>
          )}
        </IndFold>
      </div>
    </div>
  );
};

// ── Files ────────────────────────────────────────────────────────────────────

const IndFiles = ({ page, slug, onFiles }) => {
  const [busy, setBusy] = React.useState(null);
  const inputs = React.useRef({});
  const upload = (kind, list) => {
    if (!list || !list.length) return;
    setBusy(kind);
    window.AtlasApi.uploadIndustryFiles(slug, kind, Array.from(list))
      .then((r) => { onFiles(r.files); window.pushToast({ type: 'success', title: list.length === 1 ? 'File added' : `${list.length} files added` }); })
      .catch((e) => window.pushToast({ type: 'error', title: 'Upload failed', body: e.message }))
      .finally(() => { setBusy(null); if (inputs.current[kind]) inputs.current[kind].value = ''; });
  };
  const remove = (f) => {
    window.showConfirm({ title: `Remove ${f.original_filename}?`, body: 'It comes off this page.', confirmLabel: 'Remove', danger: true })
      .then((ok) => {
        if (!ok) return;
        window.AtlasApi.removeIndustryFile(slug, f.id)
          .then((r) => onFiles(r.files))
          .catch((e) => window.pushToast({ type: 'error', title: 'Could not remove the file', body: e.message }));
      });
  };
  const slots = IND_FILE_SLOTS.filter(([k]) => k !== 'other' || page.files.some((f) => f.kind === 'other') || page.can_edit);
  return (
    <div>
      {slots.map(([kind, label], i) => {
        const files = page.files.filter((f) => f.kind === kind);
        return (
          <div key={kind} style={{ padding: '10px 14px', borderTop: i ? '1px solid var(--border)' : 'none' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: files.length ? 6 : 0, flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text)' }}>{label}</span>
              {files.length === 0 && <span style={{ fontSize: 12, color: 'var(--text-4)' }}>None yet</span>}
              <span style={{ flex: 1 }} />
              {page.can_edit && (
                <>
                  <input ref={(el) => { inputs.current[kind] = el; }} type="file" multiple accept=".pptx,.ppt,.pdf,.key,.png,.jpg,.jpeg"
                    style={{ display: 'none' }} onChange={(e) => upload(kind, e.target.files)} aria-label={`Upload ${label}`} />
                  <window.Btn size="sm" variant="outline" disabled={busy === kind}
                    icon={window.Icon && window.Icon.Upload ? <window.Icon.Upload size={12} /> : null}
                    onClick={() => inputs.current[kind] && inputs.current[kind].click()}>
                    {busy === kind ? 'Uploading…' : 'Upload'}
                  </window.Btn>
                </>
              )}
            </div>
            {files.map((f) => (
              <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0', fontSize: 12.5, flexWrap: 'wrap' }}>
                <button type="button" onClick={() => window.AtlasApi.openAttachment(f.id, f.original_filename)}
                  style={{ border: 'none', background: 'transparent', padding: 0, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 500,
                           color: 'var(--accent-text)', textAlign: 'left', minWidth: 0, overflowWrap: 'anywhere' }}>
                  {f.original_filename}
                </button>
                <span style={indMuted}>
                  {f.version ? `v${f.version} · ` : ''}{f.added_by ? `${f.added_by} · ` : ''}{indDate(f.added_at)}
                </span>
                {page.can_edit && (
                  <button type="button" onClick={() => remove(f)} aria-label={`Remove ${f.original_filename}`} title="Remove from this page"
                    style={{ border: 'none', background: 'transparent', cursor: 'pointer', color: 'var(--text-4)', padding: 2, display: 'inline-flex' }}>
                    {window.Icon && window.Icon.Trash ? <window.Icon.Trash size={12} /> : '×'}
                  </button>
                )}
              </div>
            ))}
          </div>
        );
      })}
    </div>
  );
};

// ── Outreach ─────────────────────────────────────────────────────────────────

const IndOutreach = ({ page, industry }) => (
  <div style={{ padding: '6px 0 0' }}>
    <IndSub title="Email Copilot campaigns" count={page.campaigns.length}>
      {page.campaigns.length === 0 ? <IndEmpty>No Email Copilot campaigns for this industry.</IndEmpty> : (
        <IndTable minWidth={560} head={[['Campaign'], ['Status'], ['Started'], ['Sent', 'right'], ['Replied', 'right'], ['Positive', 'right']]}>
          {page.campaigns.map((c) => (
            <tr key={c.id} style={{ borderTop: '1px solid var(--border)' }}>
              <td style={indTd}>{page.is_admin ? <a href="#/email-copilot" style={indLink}>{c.name}</a> : <span style={{ fontWeight: 500 }}>{c.name}</span>}</td>
              <td style={indTd}>
                <window.Pill hue={IND_CAMPAIGN_HUE[c.status] || 'slate'} sz="sm">{c.status}</window.Pill>
                {c.mode === 'shadow' && <span style={{ ...indMuted, marginLeft: 6 }} title="Picks and qualifies, never sends">shadow</span>}
              </td>
              <td style={{ ...indTd, ...indMuted }}>{indDate(c.started_at || c.created_at)}</td>
              {['sent', 'replied', 'positive'].map((k) => (
                <td key={k} style={{ ...indTd, textAlign: 'right', fontVariantNumeric: 'tabular-nums', color: c[k] ? 'var(--text)' : 'var(--text-4)' }}>{c[k] || '—'}</td>
              ))}
            </tr>
          ))}
        </IndTable>
      )}
    </IndSub>
    <div style={{ padding: '0 14px' }}>
      {window.IndustryRulesBlock && <window.IndustryRulesBlock industry={industry} readOnly={!page.can_edit} hideRubric />}
    </div>
  </div>
);

// ── One industry (#/industries/<slug>) ───────────────────────────────────────

const IndustryPage = ({ slug }) => {
  const width = useIndWidth();
  const narrow = width < 720;
  const [page, setPage] = React.useState(null);
  const [err, setErr] = React.useState(null);
  const [build, setBuild] = React.useState(null);
  const [building, setBuilding] = React.useState(false);   // the Build drawer
  const scroller = React.useRef(null);
  const load = React.useCallback(() => {
    return window.AtlasApi.getIndustryPage(slug).then((p) => { setPage(p); setBuild(p.build || null); })
      .catch((e) => setErr(e.message || String(e)));
  }, [slug]);
  React.useEffect(() => { setPage(null); setErr(null); setBuild(null); load(); }, [load]);
  // A live build: its step trail every few seconds; the page reloads once it ends.
  window.usePolling(async () => {
    const r = await window.AtlasApi.getIndustryBuild(slug);
    const b = r && r.build;
    setBuild(b || null);
    if (!indActive(b)) load();
    return b ? `${b.status}:${(b.steps || []).length}` : 'none';
  }, { active: indActive(build), key: build ? build.id : null, first: 4000, giveUpAfter: 0, signature: (x) => x });
  const jump = (id) => {
    const el = document.getElementById(`industry-${id}`);
    if (el && scroller.current) scroller.current.scrollTo({ top: el.offsetTop - 8, behavior: 'smooth' });
  };
  // The phone bar has its own Back to the list.
  const crumb = window.AtlasDevice && window.AtlasDevice.phone ? null : (
    <a href="#/industries" style={{ fontSize: 12, color: 'var(--text-3)', textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      {window.Icon && window.Icon.ChevronL ? <window.Icon.ChevronL size={11} /> : '‹'} Industries
    </a>
  );
  if (err) {
    const missing = /404/.test(err);
    return (
      <IndScroller narrow={narrow}>
        {crumb}
        <div style={{ marginTop: 14, fontSize: 13, color: missing ? 'var(--text-2)' : 'var(--rose-fg)' }}>
          {missing ? 'No industry at this address.' : `Could not load this industry: ${err}`}
        </div>
      </IndScroller>
    );
  }
  if (!page) {
    return (
      <IndScroller narrow={narrow}>
        {crumb}
        <div style={{ marginTop: 12 }}><window.Skeleton w={260} h={22} /></div>
        <div style={{ marginTop: 16 }}><window.SkeletonRows rows={12} /></div>
      </IndScroller>
    );
  }
  const ind = page.industry;
  const v = page.valuation;
  const sections = IND_SECTIONS.filter(([k]) => k !== 'market' || page.can_view_market);
  const live = indActive(build);
  const canBuild = page.can_edit && ind.active && !live;
  const valuationPreview = live ? (build.status === 'queued' ? 'Build queued' : `Building · ${build.step_label || ''}`)
    : page.draft ? `Draft v${page.draft.version} waiting for approval`
    : v ? `${indTier(v.ranges.low)} / ${indTier(v.ranges.average)} / ${indTier(v.ranges.high)}` : 'No valuation yet';
  const sec = { marginTop: 12 };
  return (
    <IndScroller narrow={narrow} scrollRef={scroller}>
      {crumb}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 6 }}>
        <h1 style={{ fontSize: narrow ? 19 : 22, fontWeight: 650, margin: 0, color: 'var(--text)', fontFamily: 'var(--font-display)', letterSpacing: '-0.01em' }}>{ind.name}</h1>
        <IndLeadPill lead={ind.lead} />
        {!ind.active && <window.Pill hue="rose" sz="sm">Inactive</window.Pill>}
        {v && (
          <span style={{ fontSize: 12.5, color: 'var(--text-2)', fontVariantNumeric: 'tabular-nums' }}>
            {indTier(v.ranges.low)} / {indTier(v.ranges.average)} / {indTier(v.ranges.high)} {indMetric(v)}
          </span>
        )}
      </div>
      <nav aria-label="Sections" style={{ position: 'sticky', top: 0, zIndex: 5, background: 'var(--bg)', display: 'flex', gap: 2, padding: '8px 0', marginTop: 4, overflowX: 'auto', scrollbarWidth: 'none' }}>
        {sections.map(([k, label]) => (
          <button key={k} type="button" onClick={() => jump(k)}
            style={{ border: 'none', background: 'transparent', padding: '4px 9px', borderRadius: 6, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5,
                     fontWeight: 500, color: 'var(--text-2)', whiteSpace: 'nowrap' }}
            onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--surface-hover)'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}>
            {label}
          </button>
        ))}
      </nav>
      <div id="industry-valuation" style={{ marginTop: 4 }}>
        <window.Section collapsible id="industry.valuation" title="Valuation"
          action={(v || canBuild) ? (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              {v && <span style={indMuted}>v{v.version}{page.versions.length > 1 ? ` of ${page.versions.length}` : ''}</span>}
              {canBuild && (
                <window.Btn size="sm" variant="outline" onClick={() => setBuilding(true)}
                  title={`${v || page.draft ? 'Rebuild' : 'Build valuation'}: Atlas researches the trade and saves a draft for approval`}>
                  {v || page.draft ? 'Rebuild' : 'Build valuation'}
                </window.Btn>
              )}
            </span>
          ) : null}
          preview={valuationPreview}>
          <IndValuation page={page} slug={slug} narrow={narrow} onReload={load} build={build} onBuild={() => setBuilding(true)} />
        </window.Section>
        {page.can_edit && (
          <IndBuildDrawer open={building} onClose={() => setBuilding(false)} page={page} slug={slug}
            onStarted={(b) => { if (b) setBuild(b); load(); }} />
        )}
      </div>
      <div id="industry-numbers" style={sec}>
        <window.Section collapsible id="industry.numbers" title="Our numbers"
          preview={page.our_numbers.summary ? `${page.our_numbers.summary.n} closings · median ${indX(page.our_numbers.summary.median)}` : 'No closings yet'}>
          <IndNumbers page={page} narrow={narrow} />
        </window.Section>
      </div>
      {page.can_view_market && (
        <div id="industry-market" style={sec}>
          <window.Section collapsible id="industry.market" title="Market">
            <IndMarket slug={slug} narrow={narrow} />
          </window.Section>
        </div>
      )}
      <div id="industry-files" style={sec}>
        <window.Section collapsible id="industry.files" title="Files" preview={`${page.files.length} file${page.files.length === 1 ? '' : 's'}`}
          action={page.files.length ? <span style={indMuted}>{page.files.length}</span> : null}>
          <IndFiles page={page} slug={slug} onFiles={(files) => setPage((p) => ({ ...p, files }))} />
        </window.Section>
      </div>
      <div id="industry-outreach" style={sec}>
        <window.Section collapsible id="industry.outreach" title="Outreach"
          preview={`${page.campaigns.length} campaign${page.campaigns.length === 1 ? '' : 's'}`}>
          <IndOutreach page={page} industry={ind} />
        </window.Section>
      </div>
    </IndScroller>
  );
};

Object.assign(window, { IndustriesView, IndustryPage });
